import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const collection = params.get("collection") || "";
  const colour = params.get("colour") || "";
  const maxPrice = params.get("max_price") || "";
  const sort = params.get("sort") || "";
  const search = params.get("search") || "";

  useEffect(() => {
    api.get("/shopify/collections").then((r) => setCollections(r.data.filter((c) => !/default example/i.test(c.name))));
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (collection) qs.set("collection", collection);
    if (search) qs.set("search", search);
    api.get(`/shopify/products?${qs.toString()}`).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [collection, search]);

  const allColours = useMemo(() => {
    const set = new Map();
    products.forEach((p) => (p.colours || []).forEach((c) => set.set(c.name, c.swatch)));
    return [...set.entries()];
  }, [products]);

  const shown = useMemo(() => {
    let items = [...products];
    if (colour) items = items.filter((p) => (p.colours || []).some((c) => c.name.toLowerCase() === colour.toLowerCase()));
    const eff = (p) => p.sale_price || p.price;
    if (maxPrice) items = items.filter((p) => eff(p) <= Number(maxPrice));
    if (sort === "price_asc") items.sort((a, b) => eff(a) - eff(b));
    else if (sort === "price_desc") items.sort((a, b) => eff(b) - eff(a));
    return items;
  }, [products, colour, maxPrice, sort]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };
  const clearAll = () => setParams(new URLSearchParams());

  const activeCol = collections.find((c) => c.slug === collection);
  const heading = search ? `Search: "${search}"` : activeCol?.name || "Shop all";

  // Parent categories that show sub-category tiles instead of a product grid.
  // Children = any collection (not the parent) whose name matches the keyword(s).
  const SUBCAT_RULES = { "crochet-scarves-chunky-and-cosy-crochet-kerry": ["scarf"] };
  const subcats = useMemo(() => {
    const kws = SUBCAT_RULES[collection];
    if (!kws || search) return [];
    return collections.filter((c) => c.slug !== collection && kws.some((k) => c.name.toLowerCase().includes(k)));
  }, [collection, collections, search]);

  const Filters = () => (
    <div className="space-y-7" data-testid="filters">
      <div>
        <h4 className="text-xs uppercase tracking-luxe mb-3">Collections</h4>
        <div className="space-y-2 text-sm">
          <button onClick={() => update("collection", "")} className={`block ${!collection ? "text-brand-pink" : "text-muted-foreground hover:text-brand-ink"}`}>All products</button>
          {collections.map((c) => (
            <button key={c.id} onClick={() => update("collection", c.slug)} className={`block text-left ${collection === c.slug ? "text-brand-pink" : "text-muted-foreground hover:text-brand-ink"}`} data-testid={`filter-col-${c.slug}`}>{c.name}</button>
          ))}
        </div>
      </div>
      {allColours.length > 0 && (
        <div>
          <h4 className="text-xs uppercase tracking-luxe mb-3">Colour</h4>
          <div className="flex flex-wrap gap-2">
            {allColours.map(([name, swatch]) => (
              <button key={name} title={name} onClick={() => update("colour", colour === name ? "" : name)}
                className={`w-7 h-7 rounded-full border-2 ${colour === name ? "border-brand-ink" : "border-brand-border"}`} style={{ background: swatch }} data-testid={`filter-colour-${name}`} />
            ))}
          </div>
        </div>
      )}
      <div>
        <h4 className="text-xs uppercase tracking-luxe mb-3">Max price: £{maxPrice || "60"}</h4>
        <input type="range" min="5" max="60" step="1" value={maxPrice || 60} onChange={(e) => update("max_price", e.target.value === "60" ? "" : e.target.value)} className="w-full accent-brand-pink" data-testid="filter-price" />
      </div>
      <button onClick={clearAll} className="text-xs uppercase tracking-luxe underline" data-testid="clear-filters">Clear all filters</button>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title={heading} description={`Shop ${heading} — handmade crochet by Crochet Kerry.`} />
      {activeCol && !search ? (
        <div className="relative h-52 sm:h-64 rounded-sm overflow-hidden mb-8" data-testid="category-banner">
          {activeCol.image
            ? <img src={activeCol.image} alt={activeCol.name} className="absolute inset-0 w-full h-full object-cover" />
            : <div className="absolute inset-0 bg-gradient-to-br from-brand-blush via-brand-pink/60 to-brand-blush" />}
          <div className="absolute inset-0 bg-black/35" />
          <div className="relative h-full flex flex-col justify-end p-6 sm:p-8 text-white ck-fade-up">
            <h1 className="font-serif-display text-4xl sm:text-5xl" data-testid="shop-heading">{activeCol.name}</h1>
            {activeCol.description && <p className="text-white/85 mt-2 max-w-2xl text-sm">{activeCol.description}</p>}
          </div>
        </div>
      ) : (
        <div className="mb-8">
          <p className="text-xs uppercase tracking-luxe text-brand-pink mb-2">Shop</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl" data-testid="shop-heading">{heading}</h1>
          {activeCol?.description && <p className="text-muted-foreground mt-2 max-w-2xl">{activeCol.description}</p>}
        </div>
      )}

      {subcats.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="subcategory-tiles">
          {subcats.map((sc) => (
            <Link key={sc.id} to={`/shop?collection=${sc.slug}`} className="group relative h-72 sm:h-96 overflow-hidden rounded-sm" data-testid={`subcat-${sc.slug}`}>
              {sc.image
                ? <img src={sc.image} alt={sc.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                : <div className="w-full h-full bg-gradient-to-br from-brand-blush via-brand-pink/60 to-brand-blush transition-transform duration-700 group-hover:scale-105" />}
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
              <div className="absolute bottom-0 p-6 text-white">
                <h3 className="font-serif-display text-3xl mb-1">{sc.name}</h3>
                <span className="inline-flex items-center gap-2 mt-1 text-xs uppercase tracking-luxe">Explore <ArrowRight className="w-4 h-4" /></span>
              </div>
            </Link>
          ))}
        </div>
      ) : (
      <>
      <div className="flex items-center justify-between mb-6 border-b border-brand-border pb-4">
        <button onClick={() => setShowFilters(true)} className="lg:hidden flex items-center gap-2 text-sm" data-testid="open-filters"><SlidersHorizontal className="w-4 h-4" /> Filters</button>
        <p className="text-sm text-muted-foreground" data-testid="product-count">{shown.length} item{shown.length !== 1 ? "s" : ""}</p>
        <select value={sort} onChange={(e) => update("sort", e.target.value)} className="text-sm border border-brand-border rounded-sm px-3 py-2 bg-white" data-testid="sort-select">
          <option value="">Featured</option>
          <option value="price_asc">Price: low to high</option>
          <option value="price_desc">Price: high to low</option>
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
        <aside className="hidden lg:block"><Filters /></aside>
        <div>
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">{[...Array(6)].map((_, i) => <div key={i} className="aspect-[3/4] bg-brand-blush/40 rounded-sm animate-pulse" />)}</div>
          ) : shown.length === 0 ? (
            <div className="text-center py-24 text-muted-foreground" data-testid="no-products">No products found. Try adjusting your filters.</div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">{shown.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
          )}
        </div>
      </div>
      </>
      )}

      {showFilters && (
        <div className="fixed inset-0 z-[60] bg-black/50 lg:hidden" onClick={() => setShowFilters(false)}>
          <div className="absolute left-0 top-0 bottom-0 w-80 bg-white p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6"><h3 className="font-serif-display text-2xl">Filters</h3><button onClick={() => setShowFilters(false)}><X className="w-5 h-5" /></button></div>
            <Filters />
          </div>
        </div>
      )}
    </div>
  );
}
